import { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

/*
  * Font Families can be 
  * - SansBold
  * - Script (This will only apply to h1 - h3)
  
  * Heading Colors can be 
  * - Light
  * - Dark
  * - Accent
  
  * Text Colors can be 
  * - Light
  * - Dark
  
  * Text Sizing can be
  * - Large
  * - Medium (This will be our default Text sizing)
  * - Small
*/
export default css`
  // * Shared styling between all headings
  h1,
  .h1,
  h2,
  .h2,
  h3,
  .h3,
  h4,
  .h4,
  h5,
  .h5,
  h6,
  .h6 {
    color: ${themeGet('colors.black')};
    font-family: ${themeGet('fonts.primary')};
    font-weight: ${themeGet('font-weights.bold')};

    &.light {
      color: ${themeGet('colors.light-text')};
    }

    &.dark {
      color: ${themeGet('colors.dark-text')};
    }

    &.accent {
      color: ${themeGet('colors.accent-text')};
    }

    span {
      color: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      letter-spacing: inherit;
    }
  }

  h1,
  h2,
  h3 {
    &.script,
    &.prodigy-font,
    span.script,
    span.prodigy-font {
      font-family: ${themeGet('fonts.secondary')};
      font-weight: normal;
    }
  }

  h1,
  .h1 {
    font-size: 30px;
    letter-spacing: -0.2px;
    line-height: 34px;

    &.script,
    &.prodigy-font,
    span.script,
    span.prodigy-font {
      font-size: 40px;
      letter-spacing: 0;
      line-height: 44px;
    }
  }

  h2,
  .h2 {
    font-size: 26px;
    letter-spacing: 0;
    line-height: 34px;

    &.script,
    &.prodigy-font,
    span.script,
    span.prodigy-font {
      font-size: 32px;
      letter-spacing: 0;
      line-height: 36px;
    }
  }

  h3,
  .h3 {
    font-size: 20px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 26px;

    &.script,
    &.prodigy-font,
    span.script,
    span.prodigy-font {
      font-size: 26px;
      letter-spacing: 0;
      line-height: 30px;
    }
  }

  h4,
  .h4 {
    font-size: 20px;
    letter-spacing: 0;
    line-height: 28px;
  }

  h5,
  .h5 {
    font-size: 18px;
    line-height: 28px;
    letter-spacing: -0.2px;
  }

  h6,
  .h6 {
    font-size: 18px;
    line-height: 28px;
    letter-spacing: -0.2px;
  }

  // * Paragraph + span styling
  p,
  li,
  span,
  blockquote {
    color: ${themeGet('colors.black')};
    font-family: ${themeGet('fonts.primary')};
    font-size: 16px;
    letter-spacing: 0;
    line-height: 24px;

    &.light {
      color: ${themeGet('colors.light-text')};
    }

    &.dark {
      color: ${themeGet('colors.dark-text')};
    }

    // * Large text styling
    &.large {
      font-size: 18px;
      font-weight: ${themeGet('font-weights.semi-bold')};
      letter-spacing: -0.41px;
      line-height: 24px;
    }

    // * Small text styling
    &.small {
      font-size: 14px;
      letter-spacing: 0;
      line-height: 22px;
    }

    // * Styling for text hyperlinks inside of content
    a {
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      letter-spacing: inherit;
      color: ${themeGet('colors.darkest-orange')};
      text-decoration: underline;

      &:hover,
      &:focus {
        color: ${themeGet('colors.orange')};
      }
    }
  }

  a {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    span {
      color: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      letter-spacing: inherit;
    }
    &:hover,
    &:focus {
    }
  }

  a.ProdigySans span {
    padding-top: 3px;
  }

  // * Styling for hyperlinks inside of hero
  #hero p a {
    color: ${themeGet('colors.white')};
  }

  // * Styling for copy in filter hero
  #filter-hero p p {
    color: ${themeGet('colors.white')};
  }

  #filter-hero p p a {
    color: ${themeGet('colors.white')};
  }

  // // *Styling for heading heirarchy fix on /reasearch page
  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    #section-2-full-width-image-and-text-boxes-primary .gjicft h2 {
      font-size: 56px;
      letter-spacing: -0.5px;
      line-height: 56px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    h1,
    .h1 {
      font-size: 56px;
      letter-spacing: -0.5px;
      line-height: 56px;

      &.script,
      &.prodigy-font,
      span.script,
      span.prodigy-font {
        font-size: 72px;
        letter-spacing: 0;
        line-height: 84px;
      }
    }

    h2,
    .h2 {
      font-size: 36px;
      line-height: 46px;
      letter-spacing: -0.5px;

      &.script,
      &.prodigy-font,
      span.script,
      span.prodigy-font {
        font-size: 38px;
        letter-spacing: 0;
        line-height: 44px;
      }
    }

    h3,
    .h3 {
      font-size: 26px;
      line-height: 38px;
      letter-spacing: -0.5px;
      font-weight: 500;

      &.script,
      &.prodigy-font,
      span.script,
      span.prodigy-font {
        font-size: 32px;
        letter-spacing: 0.12px;
        line-height: 38px;
      }
    }

    h4,
    .h4 {
      font-size: 22px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }

    h5,
    .h5 {
      font-size: 18px;
      line-height: 28px;
      letter-spacing: -0.2px;
    }

    h6,
    .h6 {
      font-size: 18px;
      line-height: 28px;
      letter-spacing: -0.2px;
    }

    // * Paragraph + span styling
    p,
    span,
    li,
    blockquote {
      font-size: 18px;
      letter-spacing: -0.2px;
      line-height: 30px;

      // * Large text styling
      &.large {
        font-size: 22px;
        letter-spacing: -0.17px;
        line-height: 32px;
      }

      // * Small text styling
      &.small {
        font-size: 15px;
        letter-spacing: -0.2px;
        line-height: 25px;
      }
    }
  }
`;
